import { Edit, FileText, GitMerge, Plus } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  CoverageComparisonRecommendationSchema,
  CoverageSchema,
  UpdateCoverageSchema,
} from '@/services/types/coverage-types';

interface IntegrationDetailsProps {
  existingEntities: CoverageSchema[] | null;
  integration: CoverageComparisonRecommendationSchema | null;
  patch: UpdateCoverageSchema | null;
}

export const IntegrationDetails = ({
  existingEntities,
  integration,
  patch,
}: IntegrationDetailsProps) => {
  const hasIntegrationData = existingEntities || integration || patch;

  if (!hasIntegrationData) {
    return (
      <div className="text-center py-8 text-gray-500">
        <GitMerge className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p>No integration data available</p>
        <p className="text-sm mt-2">
          This mention was not processed through the integration pipeline
        </p>
      </div>
    );
  }

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'ADD_AS_NEW':
        return <Plus className="h-4 w-4 text-green-600" />;
      case 'MERGE_WITH_EXISTING':
        return <GitMerge className="h-4 w-4 text-blue-600" />;
      default:
        return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'ADD_AS_NEW':
        return 'bg-green-100 text-green-800';
      case 'MERGE_WITH_EXISTING':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Integration Recommendation */}
      {integration && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {getActionIcon(integration.action_recommended)}
              Integration Recommendation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Badge
                variant="secondary"
                className={getActionColor(integration.action_recommended)}
              >
                {integration.action_recommended.replace('_', ' ')}
              </Badge>
              {integration.target_existing_coverage_id && (
                <span className="text-sm text-gray-600">
                  Target:{' '}
                  <span className="font-mono">
                    {integration.target_existing_coverage_id.slice(-8)}
                  </span>
                </span>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Confidence Score:</span>
                <span className="font-medium">
                  {(integration.confidence_score * 100).toFixed(1)}%
                </span>
              </div>
              <Progress
                value={integration.confidence_score * 100}
                className="h-2"
              />
            </div>

            {integration.reasoning && (
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Reasoning:
                </h4>
                <p className="text-sm leading-relaxed">
                  {integration.reasoning}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Field Analysis */}
      {integration &&
        (integration.identified_overlapping_fields ||
          integration.new_information_identified) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Overlapping Fields */}
            {integration.identified_overlapping_fields && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Overlapping Fields
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(
                      integration.identified_overlapping_fields,
                    ).map(([field, matchType]) => (
                      <div
                        key={field}
                        className="flex justify-between items-center text-sm"
                      >
                        <span className="font-medium">{field}:</span>
                        <Badge
                          variant="outline"
                          className={
                            matchType === 'exact_match'
                              ? 'bg-green-100 text-green-800'
                              : matchType === 'normalized_match'
                                ? 'bg-blue-100 text-blue-800'
                                : matchType === 'partial_match'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                          }
                        >
                          {matchType.replace('_', ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* New Information */}
            {integration.new_information_identified && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">New Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(integration.new_information_identified).map(
                      ([field, value]) => (
                        <div key={field} className="text-sm">
                          <div className="font-medium text-gray-700">
                            {field}:
                          </div>
                          <div className="text-gray-600 bg-green-50 p-2 rounded mt-1">
                            {value}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

      {/* Existing Entities */}
      {existingEntities && existingEntities.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Existing Coverage Entities ({existingEntities.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {existingEntities.map((entity, index) => (
                <div
                  key={entity.id}
                  className="border rounded-lg p-4 bg-gray-50"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">Entity {index + 1}</Badge>
                      <span className="font-mono text-xs text-gray-500">
                        {entity.id.slice(-8)}
                      </span>
                      {integration?.target_existing_coverage_id ===
                        entity.id && (
                        <Badge
                          variant="secondary"
                          className="bg-blue-100 text-blue-800"
                        >
                          Target for Merge
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {entity.category && (
                      <div>
                        <span className="text-gray-600">Category: </span>
                        <span className="font-medium">
                          {entity.category.replace('_', ' ')}
                        </span>
                      </div>
                    )}

                    {entity.carrier?.name && (
                      <div>
                        <span className="text-gray-600">Carrier: </span>
                        <span className="font-medium">
                          {entity.carrier.name}
                        </span>
                      </div>
                    )}

                    {entity.policy_type && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Type:</span>
                        <span className="text-sm">{entity.policy_type}</span>
                      </div>
                    )}

                    {entity.policy_identifiers &&
                      entity.policy_identifiers.length > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Policy:</span>
                          <div className="text-right">
                            {entity.policy_identifiers.map(
                              (identifier, idIndex) => (
                                <div key={idIndex} className="text-sm">
                                  {identifier.identifier_type}:{' '}
                                  {identifier.identifier_value}
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>

                  {entity.mentions && entity.mentions.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="text-xs text-gray-500">
                        {entity.mentions.length} existing mention
                        {entity.mentions.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Patch Information */}
      {patch && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Proposed Changes (Patch)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-blue-50 p-4 rounded-md">
              <h4 className="text-sm font-medium text-blue-900 mb-3">
                Coverage ID: <span className="font-mono">{patch.id}</span>
              </h4>

              <div className="space-y-3">
                {Object.entries(patch)
                  .filter(([key]) => key !== 'id')
                  .map(([key, value]) => {
                    if (value === null || value === undefined) return null;

                    return (
                      <div key={key} className="text-sm">
                        <div className="font-medium text-blue-900 mb-1">
                          {key.replace(/_/g, ' ')}:
                        </div>
                        <div className="bg-white p-2 rounded border">
                          {typeof value === 'object' ? (
                            <pre className="text-xs whitespace-pre-wrap">
                              {JSON.stringify(value, null, 2)}
                            </pre>
                          ) : (
                            <span>{String(value)}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
